import { defineQuery, ExistsFilter, QueryFilter } from "@nova/queryable"
import { usePilotTrackingQuery } from "../PilotTrackingQueries"
import * as Icons from "@nova/component-icons"
import { formatDistance } from "@nova/format"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { MissionsTable } from "./MissionsTable"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useMemo } from "react"
import { ButtonPrimary } from "@nova/component-btn"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { CreateFlightsModal } from "./CreateFlightsModal"
import {
    getGenericWorkspacePage,
    getGenericWorkspacePageHeader,
} from "panels/routing/WorkspaceRoutes"
import { clearSelectedMissionIds } from "../redux/missions-table-slice"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"
import {
    selectChecklistFilter,
    selectDateFilter,
    selectDroneFilter,
    selectPilotFilter,
    selectTagFilter,
} from "../redux/missions-table-selectors"
import { CardCollection, StatCard } from "../CardCollection"

export const FlightsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "All Flights",
        rightItems: [
            () => {
                const confirmationModal = useConfirmationModalContext()
                const { isEditor } = useWorkspacePermissions()

                const handleAddFlightClick = useCallback(async () => {
                    await confirmationModal.confirm({
                        RenderComponent: ({ confirm, cancel }) => (
                            <CreateFlightsModal onCancel={cancel} onConfirm={confirm} />
                        ),
                    })
                }, [confirmationModal])

                if (!isEditor) return null

                return (
                    <ButtonPrimary
                        title="Flight"
                        LeftIcon={Icons.Plus}
                        onClick={handleAddFlightClick}
                    />
                )
            },
        ],
    }),
    Content: FlightsPageContent,
    maxWidth: "unbound",
})

function FlightsPageContent() {
    const dispatch = useAppDispatch()

    useEffect(() => {
        return () => {
            dispatch(clearSelectedMissionIds())
        }
    }, [dispatch])

    return (
        <div className="flex flex-col gap-4 h-full min-h-[625px] overflow-x-hidden pb-4">
            <MissionsTable.Filters />
            <MetricBoxes />
            <div className="flex-1 shrink overflow-hidden">
                <MissionsTable origin={{ page: "flights" }} />
            </div>
        </div>
    )
}

const flightMetricsQuery = defineQuery(
    (missionsFilters: QueryFilter<"missions">[], tagExistsFilters: ExistsFilter<"missions">[]) => ({
        resource: "missions",
        aggregates: [
            { field: "id", func: "COUNT", alias: "numMissions" },
            { field: "metadata.distance_flown", func: "SUM", alias: "distanceFlown" },
            { field: "metadata.airtime_hours", func: "SUM", alias: "totalAirtimeHours" },
            { field: "metadata.duration", func: "SUM", alias: "totalFlightTime" },
        ],
        filters: missionsFilters,
        existsFilters: tagExistsFilters,
    })
)

const MetricBoxes = () => {
    const checklistFilter = useAppSelector(selectChecklistFilter)
    const dateFilter = useAppSelector(selectDateFilter)
    const pilotFilter = useAppSelector(selectPilotFilter)
    const droneFilter = useAppSelector(selectDroneFilter)
    const tagFilter = useAppSelector(selectTagFilter)
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)

    const tagExistsFilter = useMemo(
        (): ExistsFilter<"missions"> => ({
            relation: "tags_link",
            disabled: tagFilter.length === 0,
            filters: [
                {
                    field: "tag_id",
                    op: "IN",
                    value: tagFilter.map((t) => t.id),
                },
            ],
        }),
        [tagFilter]
    )

    const missionsFilters = useMemo(
        (): QueryFilter<"missions">[] => [
            {
                field: "metadata.started_at",
                op: ">=",
                value: dateFilter?.from ?? "",
                disabled: !dateFilter?.from,
            },
            {
                field: "metadata.started_at",
                op: "<=",
                value: dateFilter?.to ?? "",
                disabled: !dateFilter?.to,
            },
            {
                field: "user_id",
                op: "=",
                value: pilotFilter?.id ?? "",
                disabled: !pilotFilter?.id,
            },
            {
                field: "aircraft_id",
                op: "IS NULL",
                value: null,
                disabled: droneFilter?.id !== "unassigned",
            },
            {
                field: "aircraft_id",
                op: "=",
                value: droneFilter?.id ?? "",
                disabled: !droneFilter?.id || droneFilter.id === "unassigned",
            },
            {
                field: "checklist_links.checklist_record_id",
                op: "IS NULL",
                value: null,
                disabled: !checklistFilter,
            },
        ],
        [checklistFilter, dateFilter, pilotFilter, droneFilter]
    )

    const query = useMemo(
        () => flightMetricsQuery(missionsFilters, [tagExistsFilter]),
        [missionsFilters, tagExistsFilter]
    )

    const { data, isLoading } = usePilotTrackingQuery(query)
    const loadingText = useLoadingText(isLoading)

    const stats = useMemo(() => {
        if (isLoading || !data?.[0]) {
            return [
                {
                    Icon: Icons.Drone,
                    title: "Total Flights",
                    body: loadingText,
                },
                {
                    Icon: Icons.Route,
                    title: "Distance Flown",
                    body: loadingText,
                },
                {
                    Icon: Icons.Acute,
                    title: "Total Airtime",
                    body: loadingText,
                },
                {
                    Icon: Icons.Schedule,
                    title: "Total Flight Time",
                    body: loadingText,
                },
            ]
        }

        const row = data[0]
        const numMissions = +row.numMissions
        const distanceFlownMeters = +(row.distanceFlown ?? 0)
        const totalAirtimeHrs = +(row.totalAirtimeHours ?? 0)
        const totalFlightTimeHrs = +(row.totalFlightTime ?? 0) / 3600

        return [
            {
                Icon: Icons.Drone,
                title: "Total Flights",
                body: `${numMissions} Flights`,
            },
            {
                Icon: Icons.Route,
                title: "Distance Flown",
                body: formatDistance({ value: distanceFlownMeters, unit: distanceUnit }),
            },
            {
                Icon: Icons.Acute,
                title: "Total Airtime",
                body: `${totalAirtimeHrs.toFixed(0)} hours`,
            },
            {
                Icon: Icons.Schedule,
                title: "Total Flight Time",
                body: `${totalFlightTimeHrs.toFixed(0)} hours`,
            },
        ]
    }, [data, distanceUnit, isLoading, loadingText])

    return (
        <CardCollection
            cards={stats.map((s) => (
                <StatCard key={s.title} {...s} />
            ))}
        />
    )
}
